import React, {
  type ReactElement,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import qs from 'qs';
import { useArea } from 'services/assist-api/entities/area/areas';

export const supportThemes = [
  'default',
  'wellbeing',
  'w-s',
  'math',
  'trm',
  'comp',
  'vocab',
  'listening',
  'pg',
  'stwe',
  'jstwe',
  'arbs',
  'tsp',
  'writing',
  'star'
] as const;

export type Theme = (typeof supportThemes)[number];

type ThemeContextShape = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

export const ThemeContext = createContext<ThemeContextShape>({
  theme: 'default',
  setTheme: () => {}
});

const getThemeFromUrl = (): Theme | undefined => {
  const parsed = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  return parsed.theme ? (parsed.theme as Theme) : undefined;
};

export const getTheme = (): Theme => {
  const urlTheme = getThemeFromUrl();
  return urlTheme || 'default';
};

export function ThemeProvider(props: React.PropsWithChildren): ReactElement {
  const [theme, setTheme] = useState<Theme>(getTheme());

  useEffect(() => {
    document?.body?.setAttribute('data-theme', theme);
  }, [theme]);

  const value = useMemo(() => ({ theme, setTheme }), [theme]);
  return <ThemeContext.Provider value={value} {...props} />;
}

export const useTheme = (): ThemeContextShape => useContext(ThemeContext);

const THEME_TITLES: Partial<Record<Theme, string>> = {
  wellbeing: 'Wellbeing @ School'
};

export const getThemeTitle = (theme: Theme): string | null => THEME_TITLES[theme] || null;

export function convertAreaToTheme(areaIdentifier: string): Theme {
  switch (areaIdentifier) {
    case 'w@s':
      // need to reprocess this one as @ is a special character
      return 'wellbeing';
    case 'math':
    case 'trm':
    case 'comp':
    case 'vocab':
    case 'listening':
    case 'pg':
    case 'stwe':
    case 'jstwe':
    case 'arbs':
    case 'tsp':
    case 'writing':
    case 'star':
      return areaIdentifier;
    default:
      return 'default';
  }
}

export function useApplyAreaTheme(areaId: number | string | undefined | null): void {
  const { setTheme } = useTheme();
  const areaQuery = useArea(areaId);

  useLayoutEffect(() => {
    const areaIdentifier = areaQuery.data?.json.identifier;
    // apply the area as a theme
    if (areaIdentifier) setTheme(convertAreaToTheme(areaIdentifier));
    // rest to default if this component unmounts
    return () => setTheme('default');
  }, [areaQuery, setTheme]);
}
